<template>
  <SimplePage title="顧客情報CSVアップロード" :loading="busy" storeNameFull noIcon>
    <template #main>
      <Error v-if="!isTokenAlive" :header="false" hideLogin>
        <template #description>
          <p class="small-text error">
            期限が切れたため顧客情報のアップロードはできません。
            再度アップロードを行いたい場合は、ぐるなび担当営業、もしくはカスタマーサポートサービスまでお問い合わせください。
          </p>
        </template>
      </Error>
      <div v-else class="main">
        <div class="format">
          <p>
            顧客情報を一括で登録することができます。<br />
            規定のCSVフォーマットのCSVデータを選択してください。
          </p>
          <div class="download">
            <DownloadIcon />
            <a href="/pdf/customerformat.csv" target="_blank" rel="noopener">
              CSVフォーマットのダウンロード
            </a>
          </div>
        </div>

        <div class="upload">
          <p class="title">CSVデータのアップロード</p>
          <p class="description">CSVファイルを選択してアップロードしてください。</p>
          <div
            class="drop-area"
            :class="{ enter: isEnter }"
            @dragenter="isEnter = true"
            @dragleave="isEnter = false"
            @dragover.prevent
            @drop.prevent="onFileChange"
          >
            <DownloadIcon class="icon" width="56" height="56" />
            <div class="file-select">
              ドラッグ&ドロップか
              <span @click="setCsv">
                ファイルを選択
                <input ref="csv" type="file" accept=".csv" @change="onFileChange" />
              </span>
            </div>
            <div v-if="fileName" class="file-name">
              <span>{{ fileName }}</span>
              <Button flat icon @click="deleteCsv">
                <DeleteIcon width="24" height="24" @click="deleteCsv" />
              </Button>
            </div>
          </div>
          <Button class="button" primary small :disabled="!fileName" @click="csvUpload">アップロード</Button>
        </div>

        <PropertySection
          title="CSVファイルのデータ整形に関する注意事項"
          description="データの整形にはExcelなどの表計算ソフトの利用が必要になります。CSV形式で保存してください。"
          hideButton
        >
          <template #text>
            <div class="howTo">
              <ul>
                <li>1行目の項目名は削除しないでください。</li>
                <li>2行目は記入例のサンプルになりますので、アップロード前に削除してアップロードしてください。</li>
                <li>特殊文字は登録できませんのでご利用不可となります。</li>
                <li>ファイル容量が10MBを超える場合は一度にアップロードが行えません。</li>
                <li>10MBを超える場合は登録データを分割し、複数ファイルに分けるようにしてください。</li>
                <li>
                  顧客情報CSVダウンロードからダウンロードしたCSVファイルをアップロードされた場合、「お客様関係者」「来店履歴」は取り込み対象外になります。
                </li>
              </ul>
              <DataTable
                class="details"
                :data="details"
                :size-dependencies="[details.label]"
                :columns="[
                  { key: 'label', name: '項目名' },
                  { key: 'content', name: '入力内容' },
                ]"
                :minItemHeight="80"
              >
              </DataTable>
            </div>
          </template>
        </PropertySection>
      </div>
    </template>
  </SimplePage>
</template>

<script>
import { mapState } from 'vuex'
import SimplePage from '@/components/templates/SimplePage'
import Button from '@/components/atoms/Button'
import DeleteIcon from '@/components/icons/delete'
import DownloadIcon from '@/components/icons/download'
import Dialog from '@/components/molecules/ModalDialog'
import PropertySection from '@/components/molecules/PropertySection'
import DataTable from '@/components/molecules/DataTable'
import Error from '@/views/Error'

export default {
  name: 'CustomerCsv',
  components: {
    Error,
    SimplePage,
    Button,
    DeleteIcon,
    DownloadIcon,
    PropertySection,
    DataTable,
  },
  data() {
    return {
      sizeLimit: 1024 * 1024 * 10,
      isEnter: false,
      fileName: null,
      csvFile: null,
    }
  },
  computed: {
    ...mapState({
      isTokenAlive: state => state.stores.current.store.isTokenAlive,
      busy: state => state.customers.csv.busy,
    }),
    details() {
      return [
        {
          id: 0,
          label: '登録店舗',
          content: '「1」または「0」を入力してください。\n（1=すべての店舗で表示、0=自店のみ表示）',
        },
        {
          id: 1,
          label: 'ふりがな（せい）/ ふりがな（めい）/ お名前（姓）/ お名前（名）/ 電話番号① / 電話番号②」',
          content: 'いずれかの入力が必須です。',
        },
        { label: '電話番号①/電話番号②', content: '頭の「0」の前に「’」を入力してください。\n例）’0300000000' },
        {
          id: 2,
          label: 'お客様特徴（VIP）/お客様特徴（リピーター）/お客様特徴（要注意）/お客様特徴（無断キャンセル）',
          content: 'それぞれ「1」、または空欄のままにしてください。\n（1=YES、空欄：未設定）',
        },
        {
          id: 3,
          label: '性別',
          content: '「1」「2」「3」のいずれかを必ず入力してください。\n(1=指定なし 2=男性 3=女性）',
        },
        {
          id: 4,
          label: '喫煙情報',
          content: '「1」「2」「3」のいずれかを必ず入力してください。\n（1=指定なし 2=喫煙 3=非喫煙）',
        },
        {
          id: 5,
          label: 'DM可否',
          content: '「1」「2」「3」のいずれかを必ず入力してください。\n（1=指定なし 2=受け取る 3=受け取らない）',
        },
        {
          id: 6,
          label: 'その他お客様特徴 / その他記念日',
          content:
            '複数登録する場合、「|（半角）」で区切って入力してください。\n（「|」は「Shift」＋「\\」で入力できます）\n年の入力を省略する場合、年を「0000」と入力してください。',
        },
        {
          id: 7,
          label: '顧客情報項目追加',
          content:
            '項目を追加する場合はフォーマットのAL～AZセル BA～BEセルを書き換えて挿入することができます。各列の1行目を項目名として書き加えてください。\n例）AL1に「お肉の焼き加減」と書き加えることで項目名として登録されます。',
        },
      ]
    },
  },
  methods: {
    async onFileChange(e) {
      this.isEnter = false
      const files = e.target.files || e.dataTransfer.files
      if (files[0].type !== 'text/csv' || files[0].size > this.sizeLimit) {
        await Dialog.show({
          title: 'ファイルアップロード',
          message:
            files[0].type !== 'text/csv'
              ? '選択されたファイルはアップロード対象外の形式です\nCSVファイルを選択してください'
              : '選択されたファイルの容量が10MBを超えています\n登録データを分割しデータ量を調整してください',
          okText: '閉じる',
        })
        this.$refs.csv.value = ''
        return
      }
      this.fileName = files[0].name
      this.csvFile = files[0]
    },
    setCsv() {
      this.$refs.csv.click()
    },
    deleteCsv() {
      this.fileName = null
      this.csvFile = null
      this.$refs.csv.value = ''
    },
    async csvUpload() {
      const formData = new FormData()
      formData.append('customer_csv', this.csvFile)
      if (
        (await this.$store.dispatch('customers/csv/upload', formData)) &&
        (await Dialog.show({
          title: 'ファイルアップロード',
          message: 'アップロードが完了しました。',
          okText: '閉じる',
        }))
      ) {
        this.deleteCsv()
      }
    },
  },
}
</script>

<style lang="postcss" scoped>
.main {
  display: grid;
  grid-gap: 24px;
  height: 100%;
  overflow: scroll;
  padding: 32px 24px;
}

.error {
  margin-top: 13px;
  text-align: center;
}

.download {
  align-items: center;
  display: grid;
  fill: var(--very-dark-gray);
  grid-auto-flow: column;
  justify-content: flex-start;
  margin-top: 8px;
  white-space: initial;

  a {
    font-weight: 600;
  }
}

.title {
  font-weight: 600;
}

input[type='file'] {
  display: none;
}

.upload {
  display: flex;
  flex-direction: column;
  grid-gap: 8px;
  line-height: 1;

  .description {
    font-size: 13px;
    line-height: initial;
  }

  .drop-area {
    align-items: center;
    background: var(--white);
    border: solid 2px var(--light-gray);
    border-radius: 2px;
    border-style: dashed;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    grid-gap: 8px;
    justify-content: center;
    padding: 32px;

    .icon {
      fill: var(--very-dark-gray-2);
      transform: rotate(180deg);
    }

    &.enter {
      border-color: var(--brand-color);
    }
  }

  .file-select {
    span {
      color: var(--brand-color);
      cursor: pointer;
    }
  }

  .file-name {
    align-items: center;
    border-top: solid 1px var(--light-gray);
    display: flex;
    fill: var(--brand-color);
    grid-gap: 8px;
    justify-content: center;
    padding-top: 16px;
    width: 100%;

    svg {
      margin: 0;
    }
  }

  .button {
    align-self: center;
    width: 122px;
  }
}

.howTo {
  ul {
    font-size: 12px;
    li {
      padding-left: 12px;
      position: relative;
      &::before {
        content: '・';
        left: 0;
        position: absolute;
      }
    }
  }

  .details {
    border-left: solid 1px var(--border-color);
    border-right: solid 1px var(--border-color);
    margin-top: 16px;

    >>> thead {
      font-size: 13px;
    }

    >>> tr {
      font-size: 15px;
    }

    >>> .label {
      min-width: 308px;
      padding: 0 16px;
      width: 308px;
    }

    >>> .content {
      white-space: break-spaces;
    }
  }
}
</style>
